import {
    Box, Button, Container, Paper, Skeleton, Stack, Typography
} from "@mui/material"
import { AppId } from "../../../../app/appTypes"
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { alpha, styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import Settings from '@mui/icons-material/Settings'
import {
    selectAllStructureNodes, selectStructure,
    selectStructureNodeById,
    setTreeSelectedNode, uncheckStructureNodes, checkStructureNodes
}
    from "../../../device/structureSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import React, { useCallback, useState } from "react";
import { Localized } from "@fluent/react";
import { LoadStructure } from "../../../../app/AppDataLoader";
import { TreeItem, TreeItemProps, treeItemClasses } from "@mui/x-tree-view/TreeItem";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { store } from "../../../../app/store";
import { Locale, selectSettings } from "../../../settings/settingsSlice";
import { selectRelationshipByStructureId } from "../../../device/structureRelationshipSlice";


export interface StructureTreeProps {
    multiselect: boolean
    editable: boolean
    showCounter: boolean
    onNodeChecked?: (nodeId: AppId) => void
    onNodeUnchecked?: (nodeId: AppId) => void
    onStructureSelected?: (nodeId: AppId, folder: boolean) => void
    expandToId?: AppId | null
}

export const StructureTreeView = (props: StructureTreeProps) => {
    return <LoadStructure component={<Skeleton />}>
        <StructureTreeComponent {...props} />
    </LoadStructure>
}

const CustomTreeItem = React.forwardRef(
    (props: TreeItemProps, ref: React.Ref<HTMLLIElement>) => (
        <TreeItem {...props} ref={ref} />
    ),
)

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
    [`& .${treeItemClasses.iconContainer}`]: {
        '& .close': {
            opacity: 0.3,
        },
    },
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: 15,
        paddingLeft: 18,
        borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
    },
    [`& .${treeItemClasses.label}:hover`]: {
        '& button': {
            visibility: 'visible',
        }
    },
    [`& .${treeItemClasses.label}`]: {
        '& button': {
            visibility: 'hidden',
        }
    },
}));


interface StructureTreeItemProps {
    nodeId: AppId
    showCounter: boolean
    onNodeChecked?: (nodeId: AppId) => void
    onNodeUnhecked?: (nodeId: AppId) => void
    onStructureSelected?: (nodeId: AppId, folder: boolean) => void
}

const StructureTreeItem = ({ nodeId, showCounter, onNodeChecked, onNodeUnhecked, onStructureSelected }: StructureTreeItemProps) => {
    const dispatch = useAppDispatch()
    const structure = useAppSelector(state => selectStructureNodeById(state, nodeId))
    const rel = useAppSelector(state => selectRelationshipByStructureId(state, nodeId))
    const selection = useAppSelector(state => selectStructure(state).tree.multiselection)

    const nextSelectionState = { ...selection };
    const selectedChildrenCount = rel?.succs.filter((i) => selection[i.structureId] === true || selection[i.structureId] === "indeterminate").length || 0;
    const totalChildrenCount = rel?.succs.length || 0;


    const checkParentNodes = (nodeId: string) => {
        const structureNode = selectStructureNodeById(store.getState(), nodeId);

        if (!structureNode?.parentStructureId) return;

        const parentId = structureNode.parentStructureId;
        const parentStructure = selectStructureNodeById(store.getState(), parentId);
        const siblings = parentStructure?.subStructureIds || [];
        const allSiblingsChecked = siblings.every((siblingId) => nextSelectionState[siblingId] === true);
        const anySiblingChecked = siblings.some((siblingId) => nextSelectionState[siblingId] !== undefined);

        if (allSiblingsChecked) {
            nextSelectionState[parentId] = true;
            dispatch(checkStructureNodes([parentId]));
        } else if (anySiblingChecked) {
            nextSelectionState[parentId] = "indeterminate";
            dispatch(checkStructureNodes([parentId]));
        } else {
            delete nextSelectionState[parentId]
            dispatch(uncheckStructureNodes([parentId]));
        }

        checkParentNodes(parentId);
    };

    const handleRowClick = (nodeId: string) => {
        if (onStructureSelected) {
            onStructureSelected(nodeId, hasAnyChild);
        }
    }

    if (structure === undefined) return null

    const { name, subStructureIds } = structure
    const hasAnyChild = subStructureIds.length > 0

    return <StyledTreeItem itemId={nodeId} label={
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            p: 0.5,
            pr: 0,
        }}
            onClick={() => handleRowClick(nodeId)}>

            <Box component={hasAnyChild ? FolderOpenIcon : Settings} />
            <Typography sx={{ marginLeft: 1, marginRight: 4, }}>
                <StructureName structureId={structure.id} />
                ({structure.code}) {showCounter && hasAnyChild && ` (${false ? `${selectedChildrenCount}/` : ""}${totalChildrenCount})`}
            </Typography>
        </Box>
    }>
        {subStructureIds.map(id => <StructureTreeItem
            showCounter={showCounter}
            key={id}
            nodeId={id}
            onNodeChecked={onNodeChecked}
            onNodeUnhecked={onNodeUnhecked}
            onStructureSelected={onStructureSelected}
        />)}
    </StyledTreeItem>
}

export const StructureTreeComponent = ({ showCounter, onNodeChecked, onNodeUnchecked, onStructureSelected, expandToId }: StructureTreeProps) => {
    const dispatch = useAppDispatch()
    const rootStructures = useAppSelector(state => selectAllStructureNodes(state).filter(x => x.parentStructureId === null))
    const allNonLeafStructureIds = useAppSelector(
        state => selectAllStructureNodes(state).filter(x => x.subStructureIds.length > 0)
            .map(({ id }) => id))
    const { kind: commandType } = useAppSelector(state => selectStructure(state).command)
    const { selected } = useAppSelector(state => selectStructure(state).tree)
    const [expanded, setExpanded] = useState<AppId[]>([])

    const handleExpandAllButtonClick = useCallback(() => {
        setExpanded(allNonLeafStructureIds)
    }, [allNonLeafStructureIds])
    const handleCollapseAllButtonClick = useCallback(() => {
        setExpanded([])
    }, [])
    const handleNodeToggle = useCallback((event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds)
    }, [])
    const handleNodeSelect = useCallback((_, nodeId: string | null) => {
        if (nodeId) {
            dispatch(setTreeSelectedNode(nodeId))
        }
    }, [dispatch])

    const getAncestors = useCallback((id: AppId): AppId[] => {
        const out: AppId[] = [];
        let node = selectStructureNodeById(store.getState(), id);
        const guard = new Set<AppId>();
        while (node?.parentStructureId && !guard.has(node.id as AppId)) {
            guard.add(node.id as AppId);
            out.push(node.parentStructureId as AppId);
            node = selectStructureNodeById(store.getState(), node.parentStructureId as AppId);
        }
        return out.reverse();
    }, []);

    React.useEffect(() => {
        if (!expandToId) {
            dispatch(setTreeSelectedNode(""));
            return;
        }
        const ancestors = getAncestors(expandToId);
        setExpanded(prev => {
            const next = new Set<string>(prev as unknown as string[]);
            ancestors.forEach(a => next.add(String(a)));
            return Array.from(next) as unknown as AppId[];
        });

        dispatch(setTreeSelectedNode(String(expandToId)));
    }, [expandToId, getAncestors, dispatch]);

    
    return <>
        <Stack spacing={2}>
            <Box>
                <Button onClick={handleExpandAllButtonClick}>
                    <Localized id="structure-expand-all">
                        <span>_Rozwiń wszystko</span>
                    </Localized>
                </Button>
                <Button onClick={handleCollapseAllButtonClick}>
                    <Localized id="structure-collapse-all">
                        <span>_Zwiń wszystko</span>
                    </Localized>
                </Button>
            </Box>
            <SimpleTreeView
                slots={{ collapseIcon: ArrowDropDownIcon, expandIcon: ArrowRightIcon }}
                onExpandedItemsChange={handleNodeToggle}
                expandedItems={expanded}
                selectedItems={selected ?? ""}
                multiSelect={false}
                onSelectedItemsChange={handleNodeSelect}
            >
                {rootStructures.map(struct =>
                    <StructureTreeItem
                        showCounter={showCounter}
                        key={struct.id}
                        nodeId={struct.id}
                        onNodeChecked={onNodeChecked}
                        onNodeUnhecked={onNodeUnchecked}
                        onStructureSelected={onStructureSelected}
                    />)}
            </SimpleTreeView>
        </Stack>
    </>;
}


export const StructureNodeSelector = () => {
    return <Container sx={{
        height: 'calc(100vh - 200px)',
        overflowX: 'auto',
    }} component={Paper} maxWidth="lg">
        <LoadStructure component={<Skeleton />}>
            <StructureTreeComponent multiselect={false} editable={false} showCounter={false} />
        </LoadStructure>
    </Container>
}


export const StructureName = ({ structureId }: { structureId: AppId }) => {
    const structure = useAppSelector(state => selectStructureNodeById(state, structureId))
    const { locale } = useAppSelector(selectSettings)

    if (structure === undefined) return <Localized id="not-found-or-unaccessible">Nie znaleziono lub niedostępny</Localized>

    const { name, name_EN } = structure

    let displayName: string
    switch (locale) {
        case 'en-US':
            displayName = name_EN ? name_EN : name
            break
        case 'pl':
            displayName = name
            break
        default:
            displayName = name_EN ? name_EN : name
            break
    }

    return <>{displayName}</>
}


export const getStructureName = (id: AppId, lang?: Locale) => {
    const locale = lang ? lang : selectSettings(store.getState()).locale
    const structure = selectStructureNodeById(store.getState(), id)

    if (structure === undefined) return 'Not found'

    const { name, name_EN } = structure

    switch (locale) {
        case 'en-US':
            return name_EN ? name_EN : name
        case 'pl':
            return name
        default:
            return name_EN ? name_EN : name
    }

}

export const StructureMenuItem = ({ id }: { id: AppId }) => {
    const structure = useAppSelector(state => selectStructureNodeById(state, id))

    if (structure === undefined) {
        return <span>{`Structure not found : ${id}`}</span>
    }
    else {
        return <span><StructureName structureId={id} /></span>
    }
}
